"use client";

import { Badge, Button, cn, Tabs, TabsList, TabsTrigger } from "@devhop/ui";
import { useParams } from "@tanstack/react-router";
import {
  CheckCircle2Icon,
  DollarSignIcon,
  DownloadIcon,
  FileTextIcon,
  StarIcon,
  TrendingDownIcon,
  TrendingUpIcon,
} from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatTimeAgo } from "@/shared/lib/date-time";
import { formatPrice } from "@/shared/lib/format-price";
import { AreaChart } from "@/shared/ui/AreaChart";
import { BarChart } from "@/shared/ui/BarChart";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { QueryState } from "@/shared/ui/QueryState";
import { SummaryCard } from "@/shared/ui/SummaryCard";
import type { Period } from "../api/client";
import { useMechanicPerformanceQuery, useMechanicQuery } from "../api/queries";

const PERIODS: { value: Period; label: string }[] = [
  { value: "day", label: "ວັນ" },
  { value: "week", label: "ອາທິດ" },
  { value: "month", label: "ເດືອນ" },
];

const PERIOD_LABEL: Record<Period, string> = {
  day: "ວັນ",
  week: "ອາທິດ",
  month: "ເດືອນ",
};

const DAY_LABELS_LAO: Record<string, string> = {
  MON: "ຈັນ",
  TUE: "ອັງຄານ",
  WED: "ພຸດ",
  THU: "ພະຫັດ",
  FRI: "ສຸກ",
  SAT: "ເສົາ",
  SUN: "ອາທິດ",
};

const STATUS_LABELS: Record<string, string> = {
  pending_verification: "ລໍຖ້າກວດສອບ",
  active: "ກຳລັງໃຊ້ງານ",
  suspended: "ລະງັບ",
};

const jobTrendsChartConfig: ChartConfig = {
  count: { label: "ຈຳນວນວຽກ", color: "var(--color-secondary)" },
};

const earningsChartConfig: ChartConfig = {
  target: { label: "ເປົ້າໝາຍ", color: "var(--color-muted)" },
  actual: { label: "ຕົວຈິງ", color: "var(--color-primary)" },
};

function formatCurrency(n: number): string {
  return formatPrice(n, "LAK", { locale: "en-US" });
}

function renderStars(rating: number) {
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.5;
  return (
    <span className="inline-flex items-center gap-0.5 text-[#F26522]">
      {[0, 1, 2, 3, 4].map((idx) => (
        <StarIcon
          key={idx}
          className={cn(
            "size-4",
            idx < full
              ? "fill-[#F26522]"
              : idx === full && hasHalf
                ? "fill-[#F26522]/70"
                : "text-[#F26522]/30",
          )}
          aria-hidden
        />
      ))}
    </span>
  );
}

export function MechanicPerformancePage() {
  const { id } = useParams({ from: "/app/mechanics/$id/performance" });
  const [period, setPeriod] = useState<Period>("week");

  const mechanicId = id ?? "";
  const { data: mechanic } = useMechanicQuery(mechanicId);
  const { data: performance, ...perfResult } = useMechanicPerformanceQuery(
    mechanicId,
    period,
  );

  const p = performance ?? {
    jobsCompleted: 0,
    jobsCompletedChangePercent: 0,
    quotesSent: 0,
    conversionRatePercent: 0,
    avgRating: 0,
    reviewCount: 0,
    estEarnings: 0,
    estEarningsChange: 0,
    jobTrends: [],
    earningsComparison: [],
    recentReviews: [],
  };

  const periodLabel = PERIOD_LABEL[period];
  const displayName = mechanic?.displayName ?? "—";
  const statusLabel =
    STATUS_LABELS[mechanic?.status ?? ""] ?? mechanic?.status ?? "—";

  const jobTrendsData =
    p.jobTrends.length > 0
      ? p.jobTrends.map((t) => ({ day: t.day, count: t.count }))
      : [{ day: "MON", count: 0 }];

  const earningsData =
    p.earningsComparison.length > 0
      ? p.earningsComparison
      : [
          { week: "W1", target: 0, actual: 0 },
          { week: "W2", target: 0, actual: 0 },
          { week: "W3", target: 0, actual: 0 },
          { week: "W4", target: 0, actual: 0 },
        ];

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຄິວລົງທະບຽນຊ່າງ", to: "/app/mechanics" },
            {
              label: displayName,
              to: "/app/mechanics/$id",
              params: { id: mechanicId },
            },
            { label: "ລາຍງານການປະຕິບັດງານ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-bold text-2xl tracking-tight">{displayName}</h2>
            <Badge
              variant={
                mechanic?.status === "active"
                  ? "default"
                  : mechanic?.status === "suspended"
                    ? "muted"
                    : "secondary"
              }
              className="rounded-full px-2.5 py-0.5 font-medium text-xs uppercase"
            >
              {statusLabel}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
              <TabsList className="rounded-lg border bg-muted p-1">
                {PERIODS.map(({ value, label }) => (
                  <TabsTrigger
                    key={value}
                    value={value}
                    className="rounded-md px-4 py-2 font-medium text-sm data-[state=active]:shadow-sm"
                  >
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <Button type="button" variant="outline" onClick={() => {}}>
              <DownloadIcon className="size-4" />
              ສົ່ງອອກລາຍງານ
            </Button>
          </div>
        </div>

        <QueryState result={perfResult} isEmpty={false}>
          {/* KPI cards */}
          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryCard
              variant="kpi"
              title="ວຽກທີ່ເຮັດແລ້ວ"
              color="secondary"
              icon={CheckCircle2Icon}
              items={[
                {
                  label: "ວຽກທີ່ເຮັດແລ້ວ",
                  value: p.jobsCompleted.toLocaleString(),
                },
              ]}
              subtitle={
                <span
                  className={cn(
                    "flex items-center gap-1",
                    p.jobsCompletedChangePercent >= 0
                      ? "text-success"
                      : "text-destructive",
                  )}
                >
                  {p.jobsCompletedChangePercent >= 0 ? (
                    <TrendingUpIcon className="size-3.5" />
                  ) : (
                    <TrendingDownIcon className="size-3.5" />
                  )}
                  {p.jobsCompletedChangePercent >= 0 ? "+" : ""}
                  {p.jobsCompletedChangePercent}% ເທົ່າກັບ {periodLabel} ກ່ອນໜ້າ
                </span>
              }
              subtitleClassName={
                p.jobsCompletedChangePercent >= 0
                  ? "text-success"
                  : "text-destructive"
              }
            />
            <SummaryCard
              variant="kpi"
              title="ຄຳແນະນຳທີ່ສົ່ງ"
              color="secondary"
              icon={FileTextIcon}
              items={[
                { label: "ຄຳແນະນຳທີ່ສົ່ງ", value: p.quotesSent.toLocaleString() },
              ]}
              description={`${p.conversionRatePercent}% ອັດຕາການປ່ຽນແປງ`}
            />
            <SummaryCard
              variant="kpi"
              title="ຄະແນນໂດຍສະເລ່ຍ"
              color="orange"
              icon={StarIcon}
              items={[
                {
                  label: "ຄະແນນໂດຍສະເລ່ຍ",
                  value: `${p.avgRating.toFixed(2)} / 5`,
                },
              ]}
              subtitle={
                <span className="flex items-center gap-2">
                  <span>{p.avgRating.toFixed(2)} / 5</span>
                  {renderStars(p.avgRating)}
                  <span className="text-muted-foreground text-xs">
                    ({p.reviewCount} ຄຳເຫັນ)
                  </span>
                </span>
              }
            />
            <SummaryCard
              variant="kpi"
              title="ລາຍໄດ້ປະມານ"
              color="default"
              icon={DollarSignIcon}
              items={[
                { value: formatCurrency(p.estEarnings), label: "ລາຍໄດ້ປະມານ" },
              ]}
              subtitle={
                <span
                  className={cn(
                    "flex items-center gap-1",
                    p.estEarningsChange >= 0
                      ? "text-success"
                      : "text-destructive",
                  )}
                >
                  {p.estEarningsChange >= 0 ? (
                    <TrendingUpIcon className="size-3.5" />
                  ) : (
                    <TrendingDownIcon className="size-3.5" />
                  )}
                  {p.estEarningsChange >= 0 ? "+" : ""}
                  {formatCurrency(p.estEarningsChange)} ນັບແຕ່ວັນຈັນ
                </span>
              }
              subtitleClassName={
                p.estEarningsChange >= 0 ? "text-success" : "text-destructive"
              }
            />
          </div>

          {/* Charts */}
          <div className="mb-6 grid gap-6 lg:grid-cols-2">
            <div className="rounded-xl border bg-card p-4">
              <h2 className="font-bold font-heading text-secondary text-sm uppercase tracking-wide">
                ແນວໂນ້ມວຽກ
              </h2>
              <p className="text-muted-foreground text-xs">
                ຈຳນວນວຽກທີ່ເຮັດແລ້ວຕໍ່ວັນ
              </p>
              <div className="mt-4 h-[280px] w-full">
                <AreaChart
                  data={jobTrendsData}
                  config={jobTrendsChartConfig}
                  xDataKey="day"
                  valueDataKey="count"
                  xAxisTickFormatter={(v) =>
                    DAY_LABELS_LAO[String(v)] ?? String(v)
                  }
                  fillOpacity={0.4}
                  className="h-full w-full"
                />
              </div>
            </div>

            <div className="rounded-xl border bg-card p-4">
              <h2 className="font-bold font-heading text-secondary text-sm uppercase tracking-wide">
                ການປຽບທຽບລາຍໄດ້
              </h2>
              <p className="text-muted-foreground text-xs">ລາຍໄດ້ລວມຕໍ່ອາທິດ</p>
              <div className="mt-4 flex gap-4 text-xs">
                <span className="flex items-center gap-1.5">
                  <span className="size-2.5 rounded-sm bg-muted-foreground/60" />
                  ເປົ້າໝາຍ
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="size-2.5 rounded-sm bg-[#F26522]" />
                  ຕົວຈິງ
                </span>
              </div>
              <div className="mt-2 h-[280px] w-full">
                <BarChart
                  data={earningsData}
                  config={earningsChartConfig}
                  xDataKey="week"
                  valueDataKey={["target", "actual"]}
                  className="h-full w-full"
                />
              </div>
            </div>
          </div>

          {/* Recent Customer Reviews */}
          <div className="rounded-xl border bg-card p-4">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-bold font-heading text-secondary text-sm uppercase tracking-wide">
                ຄຳເຫັນລູກຄ້າຫຼ້າສຸດ
              </h2>
              {/* <button
                type="button"
                className="text-secondary text-sm hover:underline"
                onClick={() => {}}
              >
                ເບິ່ງທັງໝົດ
              </button> */}
            </div>
            <ul className="space-y-4">
              {p.recentReviews.length === 0 ? (
                <li className="py-8 text-center text-muted-foreground text-sm">
                  ຍັງບໍ່ມີຄຳເຫັນ
                </li>
              ) : (
                p.recentReviews.map((review) => (
                  <li
                    key={review.id}
                    className="flex gap-4 rounded-lg border-border border-b pb-4 last:border-0 last:pb-0"
                  >
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-secondary/20 font-medium text-secondary text-sm">
                      {(review.reviewerName ?? "?").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm">
                        {review.reviewerName ?? "ຜູ້ໃຊ້"}
                      </p>
                      <div className="mt-1 flex items-center gap-2">
                        {renderStars(review.rating)}
                        <span className="text-muted-foreground text-xs">
                          {formatTimeAgo(review.createdAt)}
                        </span>
                      </div>
                      {review.content ? (
                        <p className="mt-2 text-muted-foreground text-sm">
                          {review.content}
                        </p>
                      ) : null}
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </QueryState>
      </Main>
    </>
  );
}
